import { type Appearance, type AppearancePatch, AppearanceSchema } from '@pm/schemas';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { loadFont } from '../fonts/catalog';
import { applyAppearance, readStoredAppearance, storeAppearance } from './apply';

interface ThemeContextValue {
  appearance: Appearance;
  /** Aplica localmente na hora; o app decide quando persistir no servidor. */
  update: (patch: AppearancePatch) => Appearance;
  /** Substitui tudo (ex.: ao receber preferências do servidor após login). */
  replace: (next: Appearance) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearance] = useState<Appearance>(() => readStoredAppearance());

  useEffect(() => {
    applyAppearance(appearance);
    storeAppearance(appearance);
    void loadFont(appearance.font_sans);
    void loadFont(appearance.font_mono);
  }, [appearance]);

  // theme=system: reagir à troca do SO.
  useEffect(() => {
    if (appearance.theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyAppearance(appearance);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [appearance]);

  const update = useCallback(
    (patch: AppearancePatch): Appearance => {
      const next = AppearanceSchema.parse({ ...appearance, ...patch });
      setAppearance(next);
      return next;
    },
    [appearance],
  );

  const replace = useCallback(
    (next: Appearance) => setAppearance(AppearanceSchema.parse(next)),
    [],
  );

  const value = useMemo(() => ({ appearance, update, replace }), [appearance, update, replace]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme precisa de <ThemeProvider>');
  return ctx;
}
